/**
 * Builds a tracking URL from a base link and a set of query parameters.
 *
 * Each param value is percent-encoded individually with `encodeURIComponent`
 * rather than running `encodeURI` over the assembled string. That's the fix:
 * `encodeURI` deliberately leaves `& = ? # +` unescaped, so a campaign name
 * containing any of those characters used to corrupt the URL (an `&` split
 * into a bogus extra param, a `#` truncated everything after it, and `+`
 * would later get silently read back as a space by GA4).
 *
 * `encodeURIComponent` encodes spaces as `%20`, matching the old (working)
 * behaviour — switching to `URLSearchParams` was deliberately avoided since
 * it encodes spaces as `+`, which would change the visible output of nearly
 * every link with a space in it.
 *
 * @param {object} input
 * @param {string} input.link - The base URL, with or without existing query
 *   string / hash fragment, with or without a protocol.
 * @param {Record<string, string | number | undefined | null>} [input.params]
 *   - Flat map of param name to value. Empty/null/undefined values are
 *   omitted from the output, matching the previous behaviour.
 * @returns {{ url: string, warnings: string[] }}
 */
export function buildUrl({ link = '', params = {} } = {}) {
  const warnings = [];

  let url = String(link ?? '').trim();

  if (!url) {
    return { url: '', warnings: ['Missing base URL'] };
  }

  // Split off the hash fragment first. Everything from here on operates on
  // the part of the URL before it, so a `?` inside the fragment (e.g. a
  // hash-routed SPA link like `#/dashboard?tab=1`) can never be mistaken
  // for a real query string.
  let fragment = '';
  const hashIndex = url.indexOf('#');
  if (hashIndex !== -1) {
    fragment = url.slice(hashIndex);
    url = url.slice(0, hashIndex);
  }

  const hasExistingQuery = url.includes('?');

  if (!/^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//.test(url) && !url.startsWith('//')) {
    warnings.push('URL has no protocol.');
  }

  const pairs = Object.entries(params)
    .filter(([, value]) => value !== '' && value !== null && typeof value !== 'undefined')
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`);

  const queryString = pairs.join('&');

  let result = url;
  if (queryString) {
    result += (hasExistingQuery ? '&' : '?') + queryString;
  }
  result += fragment;

  return { url: result, warnings };
}
